import React from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {Button, Card, Col, Row, Spin, Typography} from 'antd';
import {EnvironmentOutlined, LeftOutlined} from '@ant-design/icons';

import Comments from '../Comments';
import PageTitle from '../../../components/page/PageTitle';
import Error404 from '../../../components/errors/404';
import ErrorBoundary from '../../../components/ErrorBoundary';
import {messageType, showMessages} from '../../../helpers/message';
import {useGetSingleLocationQuery} from '../../../store/api/locationsApi';
import {BaseError} from '../../../types/apiTypes';
import {useAppSelector} from '../../../hooks/useStore';
import {selectIsStaff} from '../../../store/slices/userSlice';
import {sanitizeHTML} from '../../../helpers/misc';

import './LocationsView.less';

const { Paragraph } = Typography;

const LocationManage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const isStaff = useAppSelector(selectIsStaff);
    const locationID = Number(new URLSearchParams(location.search).get('id'));

    const {data: currentLocation, error, isFetching: isLoading} = useGetSingleLocationQuery(locationID);

    if (error) {
        if ((error as BaseError).status === 404) {
            showMessages('Record not found.', messageType.error);
            return <Error404/>;
        } else {
            showMessages((error as BaseError).data?.message, messageType.error);
        }
    }

    const onclickBack = () => {
        if (location.state) {
            const {page, perPage} = location.state;
            navigate('/fleet/location/list', {state: {page, perPage}});
        } else {
            navigate(-1);
        }
    }

    return (
        <div>
            <div className="title-manage-header">
                <Button icon={<LeftOutlined/>} onClick={onclickBack}/>
                <PageTitle title="Location Details" icon={<EnvironmentOutlined/>}/>
            </div>
            <ErrorBoundary>
                <Row gutter={16}>
                    <Col xs={24} md={12} className="location-card-column">
                        <Card title={`Location #${locationID}`} bordered>
                            <Spin spinning={isLoading}>
                                <Row className="location-card-row">
                                    <Col xs={24} md={10}>
                                        <Paragraph strong>Name:</Paragraph>
                                    </Col>
                                    <Col xs={24} md={14}>
                                        <span className="long-text-wrap">{currentLocation?.name}</span>
                                    </Col>
                                </Row>
                                <Row className="location-card-row">
                                    <Col xs={24} md={10}>
                                        <Paragraph strong>Short Description:</Paragraph>
                                    </Col>
                                    <Col xs={24} md={14}>
                                        <span className="long-text-wrap">{currentLocation?.description ?? 'N/A'}</span>
                                    </Col>
                                </Row>
                                <Row className="location-card-row">
                                    <Col xs={24} md={10}>
                                        <Paragraph strong>Latitude:</Paragraph>
                                    </Col>
                                    <Col xs={24} md={14}>
                                        {currentLocation?.latitude ?? 'N/A'}
                                    </Col>
                                </Row>
                                <Row className="location-card-row">
                                    <Col xs={24} md={10}>
                                        <Paragraph strong>Longitude:</Paragraph>
                                    </Col>
                                    <Col xs={24} md={14}>
                                        {currentLocation?.longitude ?? 'N/A'}
                                    </Col>
                                </Row>
                                <Row className="location-card-row">
                                    <Col xs={24} md={10}>
                                        <Paragraph strong>Parking Note:</Paragraph>
                                    </Col>
                                    <Col xs={24} md={14}>
                                        <span className="long-text-wrap">
                                            {currentLocation?.public_note ? sanitizeHTML(currentLocation.public_note) : 'N/A'}
                                        </span>
                                    </Col>
                                </Row>
                                {
                                    isStaff &&
                                    <Row className="location-card-row">
                                        <Col xs={24} md={10}>
                                            <Paragraph strong>Staff Location Note:</Paragraph>
                                        </Col>
                                        <Col xs={24} md={14}>
                                            <span className="long-text-wrap">
                                                {currentLocation?.private_note ? sanitizeHTML(currentLocation.private_note) : 'N/A'}
                                            </span>
                                        </Col>
                                    </Row>
                                }
                            </Spin>
                        </Card>
                    </Col>
                </Row>
                {
                    isStaff &&
                    <Row gutter={16}>
                        <Col className="location-card-column">
                            <Typography.Title level={4}>Comments</Typography.Title>
                            <Comments
                                commentableType="App\Models\Locations\Location"
                                commentableID={locationID}
                            />
                        </Col>
                    </Row>
                }
            </ErrorBoundary>
        </div>
    );
};

export default LocationManage;
